import React, { useState, useEffect, createContext, useCallback } from 'react'
import { useRouter } from 'next/router'
import { ARTICLES_CONFIG } from '../utils/constants'
import findSelectedCategory from '../utils/findSelectedCategory'
import STATIC_CONTENTS from '../utils/staticContent'
import { replaceWhitespaceLowercase } from '../utils/manipulateString'

import type { Language } from 'src/siteBuilder/locale'
import type { TypeArticleWithoutUnresolvableLinksResponse } from '@/bb-contentful-types'
import {
  filterArticles,
  filterArticlesWithNoLanguageSupport,
  findArticleCategory,
  removeDuplicates,
  sortArticlesByDate,
} from '../utils/articleUtils'

const appendAllToCategoriesList = (
  categories: ArticleProviderProps['categories'],
  name: string
): GenericCategory['fields'][] => {
  const transformedCategories = categories.map((cat) => cat.fields)
  return [{ name, articles: null }, ...transformedCategories]
}

type ArticleContextType = {
  articlesPerPage: number
  isCategorySeparationDisabled: boolean
  allArticles: TypeArticleWithoutUnresolvableLinksResponse[]
  articles: TypeArticleWithoutUnresolvableLinksResponse[]
  selectedCategory: string
  allCategories: GenericCategory['fields'][]
  changeArticleByCategory?: (selectedCat: string) => void
  setCount: React.Dispatch<React.SetStateAction<number>>
  count: number
  currentPage: number
  setCurrentPage: (currentPage: number) => void
  selectedTags: string[]
  setSelectedTags: React.Dispatch<React.SetStateAction<string[]>>
}

const articleContext = createContext<ArticleContextType>(null)

export type GenericCategory = {
  fields: {
    name: string
    featuredArticles?: TypeArticleWithoutUnresolvableLinksResponse[]
    articles: TypeArticleWithoutUnresolvableLinksResponse[]
  }
}

export type ArticleProviderProps = {
  children?: JSX.Element[] | JSX.Element
  isCategorySeparationDisabled?: boolean
  lang: Language
  categories: GenericCategory[]
  navigationOptions?: 'Show More' | 'Pagination'
}

const ArticleProvider = ({
  categories,
  children,
  isCategorySeparationDisabled = false,
  lang,
  navigationOptions,
}: ArticleProviderProps) => {
  const allCategories = isCategorySeparationDisabled
    ? []
    : appendAllToCategoriesList(categories, STATIC_CONTENTS.ALL[lang])

  const articlesPerPageObject =
    ARTICLES_CONFIG.ARTICLES_PER_PAGE[
      (navigationOptions?.toUpperCase() as Uppercase<typeof navigationOptions>) ?? 'SHOW MORE'
    ]

  const [allArticles, setAllArticles] = useState([])
  const [articles, setArticles] = useState([])
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [selectedTags, setSelectedTags] = useState([])
  const router = useRouter()

  // used to determine how many articles show per page on the pagination option and how many more articles to load on the 'SHOW MORE' option
  const [articlesPerPage, setArticlesPerPage] = useState(articlesPerPageObject[ARTICLES_CONFIG.DESKTOP])

  // only used for 'SHOW MORE' option
  const [count, setCount] = useState(ARTICLES_CONFIG.INITIAL_LOAD)

  // only used for 'PAGINATION' option
  const [currentPage, setCurrentPage] = useState(0)

  const resetPage = () => setCurrentPage(0)

  const changeArticleByCategory = (selectedCat: string) => {
    const selectedCategoryForParam = replaceWhitespaceLowercase(selectedCat)
    const params = new URL(document.location as unknown as string).searchParams
    // add query parameter to url for category

    if (!isCategorySeparationDisabled) {
      params.set('category', selectedCategoryForParam)
      history.pushState(null, null, `?${params.toString()}`)
    }

    setSelectedCategory(selectedCat)
    resetPage()
    setSelectedTags([])

    let completeArticlesToSet: TypeArticleWithoutUnresolvableLinksResponse[] = []

    // @ts-ignore

    const foundCategory = findArticleCategory(categories, selectedCat)

    if (selectedCat === STATIC_CONTENTS.ALL[lang]) {
      categories.forEach((cat) => {
        const filteredArticles = filterArticlesWithNoLanguageSupport(cat?.fields?.articles, lang)

        if (filteredArticles) completeArticlesToSet.push(...filteredArticles)
      })
    } else {
      completeArticlesToSet = filterArticlesWithNoLanguageSupport(foundCategory?.fields?.articles, lang)
    }

    completeArticlesToSet = removeDuplicates(completeArticlesToSet)
    completeArticlesToSet = filterArticles(completeArticlesToSet)
    completeArticlesToSet = sortArticlesByDate(completeArticlesToSet)

    setAllArticles(completeArticlesToSet)

    // filter out the first load of articles
    setArticles(completeArticlesToSet.slice(0, 12))
  }

  // istanbul ignore next: no need to test all options
  const handleScreenSizeChange = (): void => {
    if (window.innerWidth < 576) {
      setArticlesPerPage(articlesPerPageObject[ARTICLES_CONFIG.MOBILE])
    } else if (window.innerWidth >= 576 && window.innerWidth < 1024) {
      setArticlesPerPage(articlesPerPageObject[ARTICLES_CONFIG.TABLET])
    } else if (window.innerWidth >= 1024) {
      setArticlesPerPage(articlesPerPageObject[ARTICLES_CONFIG.DESKTOP])
    }
  }

  useEffect(() => {
    resetPage()
  }, [articlesPerPage])

  useEffect(() => {
    handleScreenSizeChange()
    // at first load, find out the category from url and select it
    const categorySelected = isCategorySeparationDisabled
      ? STATIC_CONTENTS.ALL[lang]
      : findSelectedCategory(router?.asPath, categories, lang)

    changeArticleByCategory(categorySelected)

    // Reference: https://gist.github.com/gaearon/cb5add26336003ed8c0004c4ba820eae
    window.addEventListener('resize', handleScreenSizeChange)
    return () => {
      window.removeEventListener('resize', handleScreenSizeChange)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const contextValue: ArticleContextType = {
    isCategorySeparationDisabled,
    allArticles,
    articles,
    selectedCategory,
    allCategories,
    changeArticleByCategory,
    setCount,
    count,
    currentPage,
    setCurrentPage,
    selectedTags,
    setSelectedTags,
    articlesPerPage,
  }

  return <articleContext.Provider value={contextValue}>{children}</articleContext.Provider>
}

export const articleConsumer = articleContext
export default ArticleProvider
